/**
 * B-new-9382: after a deploy, open tabs still reference lazy route chunks from the
 * previous build, which 404. Vite fires `vite:preloadError` for those; we reload once
 * per build so the tab picks up the new index.html, and never loop.
 */

export interface StaleChunkRecoveryDeps {
  window: { addEventListener: (type: string, listener: (event: Event) => void) => void }
  storage: { getItem: (key: string) => string | null; setItem: (key: string, value: string) => void }
  reload: () => void
  buildId: string | undefined
}

const FALLBACK_BUILD_ID = 'unknown'

export function stalechunkStorageKey(buildId: string): string {
  return `xeito:stale-chunk-reload:${buildId}`
}

export function installStaleChunkRecovery(deps: StaleChunkRecoveryDeps): void {
  const key = stalechunkStorageKey(deps.buildId || FALLBACK_BUILD_ID)

  deps.window.addEventListener('vite:preloadError', (event) => {
    try {
      if (deps.storage.getItem(key) !== null) return // already reloaded for this build: surface the error
      deps.storage.setItem(key, String(Date.now()))
    } catch {
      return // storage unavailable: cannot guard against a loop, so do not reload
    }
    event.preventDefault()
    deps.reload()
  })
}

/** Browser wiring; call before the router mounts. */
export function installStaleChunkRecoveryForBrowser(): void {
  let storage: StaleChunkRecoveryDeps['storage']
  try {
    storage = window.sessionStorage
  } catch {
    return // accessing sessionStorage itself can throw (disabled storage)
  }
  installStaleChunkRecovery({
    window,
    storage,
    reload: () => window.location.reload(),
    buildId: import.meta.env.VITE_APP_VERSION,
  })
}
